/**
 * Short stock phrases ("Alright, one sec.", "Hang on, still going.") synthesised once
 * and played from memory.
 *
 * They fill the gaps narration cannot: the moment right after a question, and
 * long steps where the progress update has not changed. Played live they would
 * cost a speech round trip each - itself a second of silence - so they are
 * fetched when the session starts and kept for the lifetime of the page.
 */
import { fetchPost } from "../utils/axios.tsx";

const clips = new Map<string, Promise<Blob>>();

/** Synthesises a phrase, or returns the copy already fetched. */
export function loadClip(text: string): Promise<Blob> {
  let clip = clips.get(text);
  if (!clip) {
    clip = fetchPost("/v1/text-to-speech/", { text }).then((response) =>
      response.blob(),
    );
    // A failed fetch must not be cached, or the phrase stays silent for good.
    clip.catch(() => clips.delete(text));
    clips.set(text, clip);
  }
  return clip;
}

export function preloadClips(texts: string[]): void {
  texts.forEach((text) => loadClip(text).catch(() => {}));
}

/** A random phrase from the pool, never the one just said. */
export function pickPhrase(
  pool: string[],
  previous: string | null,
  random: () => number = Math.random,
): string {
  const options = pool.length > 1 ? pool.filter((p) => p !== previous) : pool;
  return options[Math.floor(random() * options.length)];
}

/** Test seam: forget every fetched clip. */
export function __resetClipsForTests(): void {
  clips.clear();
}
